import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ListChecks, Users, Bug, DollarSign, WifiOff, ArrowUpRight, ArrowRight,
  RefreshCw, Crown, CircleDot, Monitor, Gauge, FolderKanban, FileText,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import StatCard from "@/components/dashboard/StatCard";
import { GroupedBars, TrendBars } from "@/components/dashboard/Charts";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { useRefreshAll } from "@/hooks/useRefreshAll";
import { getCompanyOverview, listCompanyRuns, listTesters } from "@/api/company";
import { listExtRuns } from "@/api/extension";
import { listExeRuns, listExeDevices } from "@/api/exe";
import { getProjects } from "@/api/projects";
import { FLOWS, ACTIVE_FLOWS, FLOW_BY_KEY, normalizeRun, flowStats, perDaySeries, pickActivityWindow, isFail } from "@/lib/flows";
import { statusCounts } from "@/lib/projectStatus";

const now = Date.now();
const iso = (h) => new Date(now - h * 3600000).toISOString();

const DEMO = {
  company: { id: "demo", name: "Acme Inc." },
  plan_id: "starter", status: "active",
  usage: { runs_this_month: 142, runs_limit: 500, projects_used: 3, projects_limit: 5, testers_used: 4, testers_limit: 5 },
  open_bugs: 7, bugs_total: 23, test_cases_total: 233, spend_usd: 2.84,
};
const DEMO_DASH = [
  { id: "d1", status: "passed", url: "https://acme.com/login", instruction: "Login happy path", created_at: iso(2), tester: "anita@acme.com", duration_seconds: 12 },
  { id: "d2", status: "failed", url: "https://acme.com/cart", instruction: "Checkout empty cart", created_at: iso(6), tester: "ben@acme.com", duration_seconds: 8 },
  { id: "d3", status: "passed", url: "https://acme.com/search", instruction: "Search results", created_at: iso(26), tester: "anita@acme.com", duration_seconds: 10 },
];
const DEMO_EXT = [
  { id: "e1", status: "passed", url: "https://app.acme.com", instruction: "Filter reports", browser: "chromium", created_at: iso(3), duration_seconds: 9, token_usage: { estimated_cost_usd: 0.02 } },
  { id: "e2", status: "failed", url: "https://app.acme.com/settings", instruction: "Update billing", browser: "chromium", created_at: iso(9), duration_seconds: 14, token_usage: { estimated_cost_usd: 0.03 } },
];
const DEMO_EXE = [
  { id: "x1", status: "passed", url: "https://internal.acme.com", instruction: "Smoke test portal", browser: "chromium", created_at: iso(1), duration_seconds: 33, token_usage: { estimated_cost_usd: 0.05 } },
  { id: "x2", status: "error", url: "https://internal.acme.com/orders", instruction: "Create order", browser: "edge", created_at: iso(20), duration_seconds: 47, token_usage: { estimated_cost_usd: 0.08 } },
];
const DEMO_DEVICES = [
  { id: "1", display_name: "QA laptop", status: "online" },
  { id: "2", label: "DESKTOP-ANITA", status: "online" },
  { id: "3", label: "DESKTOP-BEN", status: "offline" },
  { id: "4", hostname: "test-bench-01", status: "online" },
];
const DEMO_TEAM = [
  { id: "1", email: "anita@acme.com", name: "Anita Rao", role: "tester", runs: 84 },
  { id: "2", email: "ben@acme.com", name: "Ben Cole", role: "tester", runs: 41 },
  { id: "3", email: "carl@acme.com", name: "Carl Diaz", role: "tester", runs: 17 },
];
// Shaped like GET /project/ (not the older projectsMock fixture, which predates
// the test_cases / testers roll-ups this card reads).
const DEMO_PROJECTS = [
  { id: "p1", name: "Acme Web", base_url: "https://acme.com", test_cases: 128, testers: 2, bugs: 12, testing_status: "active", last_run_at: iso(5) },
  { id: "p2", name: "Acme Admin", base_url: "https://admin.acme.com", test_cases: 74, testers: 1, bugs: 7, testing_status: "running", last_run_at: iso(1) },
  { id: "p3", name: "Marketing site", base_url: "https://www.acme.com", test_cases: 31, testers: 1, bugs: 4, testing_status: "idle", last_run_at: iso(900) },
  { id: "p4", name: "Partner portal", base_url: "https://partners.acme.com", test_cases: 0, testers: 0, bugs: 0, testing_status: "not_started", last_run_at: null },
];

// What to call a paired machine. display_name is the dashboard-set friendly name,
// label is what the agent reported for itself; fall back to the hostname before
// admitting we only have an id.
function deviceName(d) {
  return d.display_name || d.label || d.hostname || "Unnamed device";
}

function initials(name, email) {
  const s = name || email || "?";
  return s.split(/[\s@.]+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}
function relTime(v) {
  try {
    const m = Math.floor((Date.now() - new Date(v).getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
  } catch (e) { return "—"; }
}

function Meter({ used, limit }) {
  const pct = Math.min(100, Math.round((used / Math.max(limit || 1, 1)) * 100));
  const tone = pct >= 90 ? "bg-rose-500" : pct >= 75 ? "bg-amber-500" : "bg-brand-500";
  return (
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Section({ children, delay = 0, className }) {
  return (
    // min-w-0: a grid item defaults to min-width:auto and will not shrink below
    // its content, so a single long instruction widened the whole column and
    // pushed its cards past the edge of the screen. Truncation inside a child
    // can't help until the item itself is allowed to be narrow.
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay, ease: "easeOut" }}
      className={cn("min-w-0", className)}>
      {children}
    </motion.div>
  );
}

export default function CompanyOverview() {
  const ov = useApiData(getCompanyOverview, DEMO);
  const dash = useApiData(() => listCompanyRuns(200), DEMO_DASH);
  const ext = useApiData(() => listExtRuns(200), DEMO_EXT);
  const exe = useApiData(() => listExeRuns(200), DEMO_EXE);
  const devices = useApiData(listExeDevices, DEMO_DEVICES);
  // listTesters, not listTeam: the team endpoint returns the WHOLE roster, so a
  // card headed "Top testers" was ranking the admin, the project manager and
  // the developers alongside them.
  const team = useApiData(listTesters, DEMO_TEAM);
  // GET /project/ is unpaginated for a company-wide role, so these roll-ups cover
  // every project — not a truncated sample. The suite size per project rides along
  // with the list, so the card below costs one request and no extra queries.
  const projects = useApiData(getProjects, DEMO_PROJECTS);

  const o = ov.data || DEMO;
  const u = o.usage || DEMO.usage;
  const demo = ov.demo || dash.demo || ext.demo || exe.demo || projects.demo;

  // Normalize each flow's runs into one shape.
  const runsByFlow = useMemo(() => ({
    dashboard: (Array.isArray(dash.data) ? dash.data : []).map((r) => normalizeRun(r, "dashboard")),
    extension: (Array.isArray(ext.data) ? ext.data : []).map((r) => normalizeRun(r, "extension")),
    exe: (Array.isArray(exe.data) ? exe.data : []).map((r) => normalizeRun(r, "exe")),
  }), [dash.data, ext.data, exe.data]);

  const allRuns = useMemo(
    () => [...runsByFlow.dashboard, ...runsByFlow.extension, ...runsByFlow.exe],
    [runsByFlow]
  );

  const stats = useMemo(() => flowStats(allRuns), [allRuns]);
  // GET /company-admin/overview already sums this month's spend across ALL THREE
  // runners (test_runs + exe_runs + extension_runs). Adding the extension and exe
  // totals on top of it counted both of them twice — and did so from the last 200
  // runs of each, which is not the same month window the card claims. The server
  // aggregate is the whole number; nothing is missing from it.
  const spend = o.spend_usd || 0;
  // One window for every chart on the page, chosen from ALL runs: per-flow
  // windows would put two cards on different x-axes and make them look
  // comparable when they are not.
  const activityDays = useMemo(() => pickActivityWindow(allRuns), [allRuns]);
  const combinedSpark = useMemo(() => perDaySeries(allRuns, activityDays).data, [allRuns, activityDays]);

  // ACTIVE_FLOWS, not FLOWS: the paused cloud flow can never gain another run, so
  // its card and its bar in the activity chart were fixed at zero. The aggregate
  // KPIs and Latest runs below still include its history — those runs happened.
  const perFlow = useMemo(() => ACTIVE_FLOWS.map((f) => {
    const runs = runsByFlow[f.key];
    return { ...f, stats: flowStats(runs), series: perDaySeries(runs, activityDays) };
  }), [runsByFlow, activityDays]);

  // The CARD row shows ALL flows (not just ACTIVE_FLOWS): the paused cloud/Dashboard flow is
  // rendered here but DISABLED — same as the tester dashboard — rather than hidden. perFlow
  // above stays ACTIVE_FLOWS so the zero dashboard series doesn't reappear in the chart below.
  const flowCards = useMemo(() => FLOWS.map((f) => {
    const runs = runsByFlow[f.key];
    return { ...f, stats: flowStats(runs), series: perDaySeries(runs, activityDays) };
  }), [runsByFlow, activityDays]);
  const dashBugs = useMemo(
    () => runsByFlow.dashboard.filter((r) => isFail(r.status)).length, [runsByFlow]);

  const stackedSeries = perFlow.map((f) => ({ key: f.key, label: f.label, color: f.bar, data: f.series.data }));
  const activityLabels = perFlow[0].series.labels;
  const activityTitles = perFlow[0].series.titles;
  // Say WHY the axis widened, so a 30-day chart is not mistaken for the
  // 7-day one it replaces.
  const windowNote = activityDays === 7
    ? "last 7 days"
    : `last ${activityDays} days · no runs in the last 7`;

  const devList = Array.isArray(devices.data) ? devices.data : [];
  const onlineDevices = devList.filter((d) => String(d.status).toLowerCase() === "online");
  const online = onlineDevices.length;

  const teamList = Array.isArray(team.data) ? team.data : [];
  // Belt and braces behind the server-side ?role=tester filter, and drop anyone
  // with no runs: a "top" list is a ranking, and four names all showing 0 says
  // nothing except that the number was never being counted (it wasn't — the
  // count only covered the paused cloud runner).
  const topTesters = teamList
    .filter((t) => String(t.role || "").toLowerCase() === "tester" && (t.runs || 0) > 0)
    .sort((a, b) => (b.runs || 0) - (a.runs || 0))
    .slice(0, 5);
  const maxTR = Math.max(1, ...topTesters.map((t) => t.runs || 0));

  const recent = [...allRuns].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6);

  // Memoised: a fresh [] on every render would restart the status roll-up below
  // on every render too.
  const projectList = useMemo(
    () => (Array.isArray(projects.data) ? projects.data : []),
    [projects.data],
  );
  const statuses = useMemo(() => statusCounts(projectList), [projectList]);

  const near = (used, limit) => used / Math.max(limit || 1, 1) >= 0.8;
  const overLimit = near(u.runs_this_month, u.runs_limit) || near(u.testers_used, u.testers_limit) || near(u.projects_used, u.projects_limit);

  const { refreshing, refresh: refreshAll } = useRefreshAll(
    [ov.reload, dash.reload, ext.reload, exe.reload, devices.reload, team.reload, projects.reload],
    { label: "Overview" },
  );

  // Flow-specific highlight stat shown on each flow card. The dashboard card is shown but
  // disabled (like the tester dash), so it keeps its open-bugs line for parity.
  const flowExtra = {
    dashboard: { icon: Bug, text: `${dashBugs} open bug${dashBugs === 1 ? "" : "s"}` },
    extension: { icon: Monitor, text: `${runsByFlow.extension.filter((r) => r.browser).length} headed runs` },
    exe: { icon: CircleDot, text: `${online}/${devList.length || 0} PCs online` },
  };

  return (
    <>
      <PageHeader
        title={`${o.company?.name || "Company"} overview`}
        subtitle="Testing activity across all three flows — Dashboard, Extension, and Exe."
        actions={
          <>
            <Badge className="bg-brand-100 capitalize text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
              {String(o.plan_id || "free_trial").replace(/_/g, " ")} · {o.status}
            </Badge>
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing}>
              <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </>
        }
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {overLimit && (
        <Link to="/admin-co/billing" className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50/70 px-4 py-3 text-sm text-brand-800 transition-colors hover:bg-brand-100/70 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-200">
          <span>You&apos;re approaching your plan limits. Upgrade to add capacity for your team.</span>
          <span className="inline-flex shrink-0 items-center gap-1 font-semibold">Upgrade plan <ArrowUpRight size={15} /></span>
        </Link>
      )}

      {/* Hero KPIs — combined across all flows.
          `test_cases_total` and `bugs_total` are server aggregates over the whole
          company, NOT sums of the run/project lists fetched below: those lists are
          capped at 200 rows per flow, so deriving headline figures from them would
          quietly under-report on any company past that cap. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard icon={FolderKanban} label="Projects" value={u.projects_used ?? 0} accent="emerald" sub={`of ${u.projects_limit ?? "—"} allowed`} />
        <StatCard icon={FileText} label="Test cases" value={Number(o.test_cases_total ?? 0).toLocaleString()} accent="sky" sub="current suites" delay={0.05} />
        <StatCard icon={ListChecks} label="Total runs" value={stats.total.toLocaleString()} accent="brand" sub="across all flows" spark={combinedSpark} sparkTitles={activityTitles} delay={0.1} />
        <StatCard icon={Gauge} label="Pass rate" value={`${stats.passRate}%`} accent="emerald" sub={`${stats.passed} passed`} delay={0.15} />
        <StatCard icon={Bug} label="Bugs found" value={Number(o.bugs_total ?? 0).toLocaleString()} accent="rose" sub={`${o.open_bugs ?? 0} open`} delay={0.2} />
        <StatCard icon={DollarSign} label="Spend" value={`$${spend.toFixed(2)}`} accent="amber" sub="this month, all flows" delay={0.25} />
      </div>

      {/* Where every project stands right now. Statuses nobody is in are omitted
          — a strip of six zeroes tells the reader nothing. Each chip opens the
          Projects table filtered to that status. */}
      {statuses.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white/60 px-4 py-3 dark:border-white/10 dark:bg-white/5">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Testing status</span>
          {statuses.map((s) => (
            <Link
              key={s.key}
              to={`/admin-co/projects?status=${encodeURIComponent(s.label)}`}
              className="transition-opacity hover:opacity-80"
            >
              <Badge tone={s.tone} className="whitespace-nowrap">
                {s.live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
                {s.count} {s.label.toLowerCase()}
              </Badge>
            </Link>
          ))}
        </div>
      )}

      {/* Flow cards — the signature row */}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {flowCards.map((f, i) => {
          const Extra = flowExtra[f.key].icon;
          // The paused cloud/Dashboard flow is SHOWN but disabled — a plain <div>, not a Link,
          // greyed + "temporarily disabled", exactly like the tester dashboard.
          const off = !!f.disabled;
          const Card = off ? "div" : Link;
          const cardProps = off
            ? { "aria-disabled": "true", title: "Temporarily disabled" }
            : { to: `/admin-co/activity?flow=${f.key}` };
          return (
            <Section key={f.key} delay={i * 0.06}>
              <Card {...cardProps} className={cn("card group relative block h-full overflow-hidden p-5", off && "cursor-not-allowed opacity-70")}>
                <div className={cn("pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br opacity-60 blur-2xl", f.grad)} />
                <div className="relative flex items-center justify-between">
                  <span className={cn("inline-flex items-center gap-2 rounded-lg px-2.5 py-1 text-sm font-semibold", f.chip)}>
                    <f.icon size={16} /> {f.label}
                  </span>
                  {off ? (
                    <span className="shrink-0 text-[0.625rem] font-semibold lowercase text-rose-500 dark:text-rose-400">temporarily disabled</span>
                  ) : (
                    <ArrowRight size={16} className="text-slate-300 transition-colors group-hover:text-slate-500 dark:text-slate-600" />
                  )}
                </div>
                <div className="relative mt-4 flex items-end justify-between">
                  <div>
                    <p className="font-display text-3xl font-bold text-slate-900 dark:text-white">{f.stats.total}</p>
                    <p className="text-xs text-slate-400">runs</p>
                  </div>
                  <div className="text-right">
                    <p className={cn("font-display text-xl font-bold", f.text)}>{f.stats.passRate}%</p>
                    <p className="text-xs text-slate-400">pass rate</p>
                  </div>
                </div>
                <div className="relative mt-3">
                  <TrendBars data={f.series.data} titles={f.series.titles} height={30} color={f.bar} />
                </div>
                <div className="relative mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <Extra size={13} /> {flowExtra[f.key].text}
                </div>
              </Card>
            </Section>
          );
        })}
      </div>

      {/* Activity (stacked by flow) + device health / testers rail */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Section className="lg:col-span-2">
          <div className="card h-full p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Activity by flow</h3>
                <p className="text-xs text-slate-400">runs per day · {windowNote}</p>
              </div>
              <Link to="/admin-co/analytics" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
                Analytics <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-5">
              <GroupedBars labels={activityLabels} titles={activityTitles} series={stackedSeries}
                empty={`No runs in the last ${activityDays} days.`} />
            </div>
          </div>
        </Section>

        <Section delay={0.08}>
          <div className="card flex h-full flex-col gap-4 p-5">
            <div>
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><CircleDot size={15} className="text-violet-500" /> Exe devices</h3>
                <span className="text-xs text-slate-400">{online}/{devList.length || 0} online</span>
              </div>
              {/* The pill strip that used to sit here rendered one anonymous
                  "online"/"offline" chip per device — no name, no owner, no last
                  seen — so ten identical grey pills said nothing the count in the
                  header doesn't already say. Name the machines that are actually
                  up instead. */}
              <div className="mt-3">
                {devList.length === 0 ? (
                  <p className="text-xs text-slate-400">No devices paired.</p>
                ) : onlineDevices.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    No agent online right now — {devList.length} paired.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {onlineDevices.slice(0, 4).map((d) => (
                      <li key={d.id} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                        <span className="truncate">{deviceName(d)}</span>
                      </li>
                    ))}
                    {onlineDevices.length > 4 && (
                      <li className="text-xs text-slate-400">+{onlineDevices.length - 4} more online</li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 dark:border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Top testers</h3>
                <Link to="/admin-co/team" className="text-xs font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">Team</Link>
              </div>
              <div className="mt-3 space-y-2.5">
                {topTesters.length === 0 ? <p className="text-xs text-slate-400">No tester runs yet.</p> : topTesters.map((t, i) => (
                  <div key={t.id || t.email} className="flex items-center gap-2.5">
                    <span className="relative grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.625rem] font-bold text-white">
                      {initials(t.name, t.email)}
                      {i === 0 && <Crown size={10} className="absolute -right-1 -top-1 text-amber-400" fill="currentColor" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">{t.name || (t.email || "").split("@")[0]}</p>
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                        <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-400" style={{ width: `${Math.round(((t.runs || 0) / maxTR) * 100)}%` }} />
                      </div>
                    </div>
                    <span className="shrink-0 text-xs font-semibold text-slate-800 dark:text-white tabular-nums">{(t.runs || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* Latest runs (mixed, flow badge) + quick actions / plan usage */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Section className="lg:col-span-2">
          <div className="card h-full p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Latest runs</h3>
              <Link to="/admin-co/activity" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">Runs &amp; bugs <ArrowRight size={15} /></Link>
            </div>
            <div className="mt-3 divide-y divide-slate-100 dark:divide-white/5">
              {recent.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">No runs yet.</p> : recent.map((r) => {
                const f = FLOW_BY_KEY[r.flow];
                return (
                  <div key={`${r.flow}-${r.id}`} className="flex items-center gap-3 py-2.5">
                    <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", f.chip)}><f.icon size={15} /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-white">{r.instruction || r.url}</p>
                      <p className="truncate text-xs text-slate-400">{f.label} · {r.tester || r.browser || "—"} · {relTime(r.createdAt)}</p>
                    </div>
                    <StatusBadge status={r.status} className="shrink-0" />
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        <Section delay={0.08}>
          <div className="card flex h-full flex-col p-5">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Plan usage</p>
            <div className="mt-3 space-y-3">
              {[
                { label: "Runs this month", used: u.runs_this_month, limit: u.runs_limit, icon: ListChecks },
                { label: "Seats", used: u.testers_used, limit: u.testers_limit, icon: Users },
                // projects_used / projects_limit were already in the overview
                // payload and rendered nowhere, so the one quota that gates
                // creating a project was invisible until it was hit.
                { label: "Projects", used: u.projects_used, limit: u.projects_limit, icon: FolderKanban },
              ].map((m) => (
                <div key={m.label}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1.5 text-slate-500 dark:text-slate-400"><m.icon size={13} /> {m.label}</span>
                    <span className="font-semibold text-slate-700 dark:text-slate-200">{m.used} / {m.limit}</span>
                  </div>
                  <Meter used={m.used} limit={m.limit} />
                </div>
              ))}
            </div>
            {/* justify-center so the label sits centred once the button is full
                width, instead of hugging the left edge of a wide pill. */}
            <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4 dark:border-white/10">
              <Link to="/admin-co/team" className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-brand-500"><Users size={16} /> Manage team</Link>
              <Link to="/admin-co/analytics" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"><ListChecks size={16} /> View analytics</Link>
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}
